import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import type { SubscriptionDetail } from './models/subscription-models';

export const subscriptionQueryKeys = {
  me: ['subscription', 'me'] as const,
};

function errorMessage(err: unknown): string {
  const fromApi = axios.isAxiosError(err)
    ? (err.response?.data as { message?: string } | undefined)?.message
    : undefined;
  return fromApi || 'Gagal mengambil data langganan.';
}

async function fetchSubscription(signal?: AbortSignal): Promise<SubscriptionDetail | null> {
  const { data } = await axiosInstance.get<{ data: SubscriptionDetail | null }>(apiUrl.subscription.me, { signal });
  return data.data ?? null;
}

/**
 * The caller's own subscription. Backed by react-query so the header badge,
 * the shell banner and the billing pages all read one cached answer instead
 * of each firing their own request on every navigation; a page that changes
 * the subscription calls `refetch` and every consumer updates together.
 */
export default function useSubscription() {
  const query = useQuery({
    queryKey: subscriptionQueryKeys.me,
    queryFn: ({ signal }) => fetchSubscription(signal),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const error = query.error ? errorMessage(query.error) : null;

  return {
    data: query.data ?? null,
    loading: query.isPending,
    error,
    refetch: query.refetch,
  };
}
